import { useI18n } from 'vue-i18n'
import { supportedLocales } from '../plugins/i18n'
import { computed, ref } from 'vue'
import { getIntlLocales } from '../utils/format'

const localeToIsoMap = Object.freeze({
  'en': 'gb',
  'sv': 'se',
  'am': 'et',
  'ar': 'sa',
  'az': 'az',
  'bg': 'bg',
  'bn': 'bd',
  'bs': 'ba',
  'ceb': 'ph',
  'cs': 'cz',
  'da': 'dk',
  'de': 'de',
  'el': 'gr',
  'es': 'es',
  'et': 'ee',
  'fa': 'ir',
  'fi': 'fi',
  'fr': 'fr',
  'hi': 'in',
  'hr': 'hr',
  'hu': 'hu',
  'hy': 'am',
  'id': 'id',
  'ig': 'ng',
  'is': 'is',
  'it': 'it',
  'ja': 'jp',
  'jw': 'id',
  'ka': 'ge',
  'kk': 'kz',
  'km': 'kh',
  'ko': 'kr',
  'ku': 'iq',
  'lt': 'lt',
  'lv': 'lv',
  'mg': 'mg',
  'mk': 'mk',
  'ml': 'in',
  'mr': 'in',
  'ms': 'my',
  'my': 'mm',
  'ne': 'np',
  'nl': 'nl',
  'no': 'no',
  'ny': 'mw',
  'pa': 'pk',
  'pl': 'pl',
  'ps': 'af',
  'pt': 'pt',
  'ro': 'ro',
  'ru': 'ru',
  'sd': 'pk',
  'si': 'lk',
  'sk': 'sk',
  'sl': 'sl',
  'so': 'so',
  'sq': 'al',
  'sr': 'sr',
  'su': 'id',
  'sw': 'ke',
  'ta': 'in',
  'te': 'in',
  'th': 'th',
  'tl': 'ph',
  'tr': 'tr',
  'uk': 'ua',
  'ur': 'pk',
  'uz': 'uz',
  'vi': 'vn',
  'yo': 'ng',
  'zh-cn': 'cn',
  'zh-tw': 'cn',
})

export function useLocale() {
  const { t, locale } = useI18n()

  const manuallyTranslatedLanguages = ['en', 'sv']
  const filteredSupportedLocales = supportedLocales.filter((x): x is string => typeof x === 'string')
  const machineTranslatedLanguages = filteredSupportedLocales.filter((x) => !manuallyTranslatedLanguages.includes(x))

  const rawLocales = filteredSupportedLocales.map((code) => ({
    code,
    flag: code in localeToIsoMap ? localeToIsoMap[code as keyof typeof localeToIsoMap] : code,
    isMachineTranslated: machineTranslatedLanguages.includes(code),
  }))

  const priority = ['en', 'sv']
  const sorted = [...rawLocales].sort((a, b) => {
    const aIndex = priority.indexOf(a.code)
    const bIndex = priority.indexOf(b.code)
    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex)
    }
    return a.code.localeCompare(b.code, getIntlLocales())
  })

  const allLocales = ref(
    sorted.map((item) => ({
      ...item,
      name: t(`language.${item.code}`) || item.code,
    }))
  )

  const currentLocale = computed(() => {
    const current = locale.value
    const match = allLocales.value.find((x) => x.code === current)
    return (
      match ?? {
        code: current,
        name: t(`language.${current}`) || current,
        flag: current in localeToIsoMap ? localeToIsoMap[current as keyof typeof localeToIsoMap] : current,
        isMachineTranslated: machineTranslatedLanguages.includes(current),
      }
    )
  })

  return {
    locale,
    t,
    allLocales,
    currentLocale,
    machineTranslatedLanguages,
  }
}
